import AppContext from "PFApp/app_context";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import { SavedViewsContextProvider } from "PFApp/components/filters/saved_views_context/saved_views_context";
import Toggle from "PFComponents/toggle/toggle";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { FeatureFlag } from "PFTypes";
import { PageTarget } from "PFTypes/saved_filters";
import React, { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { BulkSelectOverlay } from "../bulk_select_overlay/bulk_select_overlay";
import TableProperties from "../table_properties";
import TablePropertiesButton from "../table_properties_button";
import Tabs from "../tabs";
import css from "./engagements_controls.module.scss";
import { useEngagementsColumns } from "./use_engagements_columns";

const viewsKey = PageTarget.WorkflowEngagements;

const EngagementsControls = () => {
  const {
    store: {
      pages: {
        workflowPage: {
          engagements: {
            data: { meta, loading },
            updateFilter,
            resetFilters,
            clearFilters,
            noFiltersSelected,
            progressView,
            setProgressView,
            columns,
            setColumns,
            groupBy,
            setGroupBy,
            selectedIds
          }
        }
      }
    }
  } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();
  const { templates } = currentProfile;
  const isEnabled = useIsFeatureEnabled();
  const stringify = useDeterministicStringify();
  const portalRef = useRef(null);

  const { t } = useTranslation("workflow", { keyPrefix: "parts.engagements" });
  const engagementTemplate = templates.find(({ key }) => key === "engagement");

  const blockedProperties = ["duration"];
  const { defaultColumns } = useEngagementsColumns(blockedProperties);

  const [showFilters, setShowFilters] = useState(false);
  const [showTableProperties, setShowTableProperties] = useState(false);

  const toggleTableProperties = () => setShowTableProperties((prev) => !prev);

  const isSoldFilterEnabled = isEnabled(FeatureFlag.SoldFilter);
  const isChargeableFilterEnabled = isEnabled(FeatureFlag.ChargeableFilter);

  const blockedFilters = useMemo(
    () => [...(isSoldFilterEnabled ? [] : ["sold"]), ...(isChargeableFilterEnabled ? [] : ["chargeable"])],
    [isSoldFilterEnabled, isChargeableFilterEnabled]
  );

  return (
    <SavedViewsContextProvider viewsKey={viewsKey}>
      <div ref={portalRef} />
      <div className={css.header}>
        {!!selectedIds?.length && <BulkSelectOverlay activityType="engagement" />}
        <Tabs qaId="WorkflowPageEngagementsTabs" />
        <div className={css.controls}>
          <FiltersButton
            filtersAreClear={noFiltersSelected}
            onClick={() => setShowFilters((prev) => !prev)}
            qaId="WorkflowPageFilterButton"
          />
          {!progressView && (
            <TablePropertiesButton isActive={showTableProperties} onClick={toggleTableProperties} />
          )}
          <Toggle
            inline
            label={t("progressView")}
            checked={progressView}
            onChange={() => setProgressView(!progressView)}
            qaId="WorkflowPageProgressViewToggle"
          />
        </div>
        <TableProperties
          isVisible={showTableProperties}
          onCloseClick={toggleTableProperties}
          template={engagementTemplate}
          selectedColumns={columns}
          defaultColumns={defaultColumns}
          onColumnsChange={setColumns || function () {}}
          selectedGroupBy={groupBy}
          onGroupByChange={setGroupBy}
          blockedProperties={blockedProperties}
        />
        {showFilters && (
          <div className={css.filters}>
            <FiltersBox
              style={{ position: "relative", width: "100%" }}
              blockedList={blockedFilters}
              isLoading={loading}
              meta={meta}
              handleFilterChange={updateFilter}
              handleFiltersChange={resetFilters}
              clearFilters={clearFilters}
              filtersAreClear={noFiltersSelected}
              closeFilters={() => setShowFilters(false)}
              clearRefValue={stringify(meta.filters)}
              viewsKey={viewsKey}
              allowToClose
              portalRef={portalRef}
            />
          </div>
        )}
      </div>
      {!noFiltersSelected && (
        <div className={css.preview}>
          <FiltersPreview
            meta={meta}
            viewsKey={viewsKey}
            blockedList={blockedFilters}
            filtersAreClear={noFiltersSelected}
            clearFilters={() => clearFilters()}
            onFilterChange={updateFilter}
            onMoreClicked={() => setShowFilters(true)}
          />
        </div>
      )}
    </SavedViewsContextProvider>
  );
};

export default EngagementsControls;
